import React, { useState } from "react";
import { AccessPermissionContext } from "../../../providers/AccessPermissionsProvider";
import NotFound from "../../../common/pages/NotFound";
import PageContainer from "../../../themes/components/PageContainer";
import PageHeading from "../../../themes/components/PageHeading";
import { AnalyticsContext } from "../../../providers/AnalyticsProvider";
import { useSecureFileUpload } from "../hooks/useSecureFileUpload";
import SecureDropzone from "../components/SecureDropzone";
import UploadSuccessModal from "../components/UploadSuccessModal";
import UploadErrorModal from "../components/UploadErrorModal";

const SecureDocumentUploadsPage: React.FC = () => {
  const [modalOpened, setModalOpened] = useState(false);

  const { hasPermissions } = React.useContext(AccessPermissionContext);
  const { trackDocumentsEvent } = React.useContext(AnalyticsContext);

  const maxSizeEnv: string =
    process.env.REACT_APP_MAX_SECURE_FILE_SIZE_MB || "50";
  const maxSizeInMb: number = parseInt(maxSizeEnv, 10);
  const maxSizeInBytes = maxSizeInMb * 1024 ** 2;

  const {
    file,
    uploadProgress,
    isRequestingSignedUrl,
    errorMessage,
    handleFileSelect,
    handleFileReject,
    resetUpload,
  } = useSecureFileUpload();

  React.useEffect(() => {
    if (uploadProgress === 100) {
      setModalOpened(true);
    }
  }, [uploadProgress]);

  const handleDrop = (files: File[]): void => {
    trackDocumentsEvent({
      event: `Select_File_CTA`,
      description: "User clicks on the upload file button on the upload view",
      event_type: "CTA:Click",
      type: "Secure Document Uploads",
      job: null,
    });
    handleFileSelect(files);
  };

  if (!hasPermissions) {
    return <NotFound />;
  }

  return (
    <PageContainer className="h-screen flex flex-col">
      <PageHeading title="Secure File Upload" />

      <SecureDropzone
        file={file}
        uploadProgress={uploadProgress}
        isLoading={isRequestingSignedUrl}
        maxSizeInBytes={maxSizeInBytes}
        onDrop={handleDrop}
        onReject={handleFileReject}
      />

      <UploadSuccessModal
        opened={modalOpened}
        onClose={() => {
          setModalOpened(false);
          resetUpload();
        }}
      />

      <UploadErrorModal
        opened={!!errorMessage}
        errorMessage={errorMessage || ""}
        maxSizeInMb={maxSizeInMb}
        onClose={() => {
          resetUpload();
        }}
        onRetry={() => {
          resetUpload();
        }}
      />
    </PageContainer>
  );
};

export default SecureDocumentUploadsPage;
